import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  getSendingSetup,
  SendingSetup,
  updateSendingSetup,
  verifySendingSetup,
} from "../../../../helpers/sending_setup_helper";
import {
  ensureUiStateLoaded,
  persistSendingSetupWizardMode,
  peekUiState,
} from "../../../../helpers/ui_state_helper";
import SendingSetupEmptyState from "./SendingSetupEmptyState";
import SendingSetupProgress, {
  SendingSetupStepLetter,
} from "./SendingSetupProgress";
import SendingSetupDnsTable from "./SendingSetupDnsTable";
import SendingSetupFormModal from "./SendingSetupFormModal";
import SendingSetupLinkPreview from "./SendingSetupLinkPreview";

/**
 * Setup de envio do tenant — GET/PUT /me/sending-setup.
 * Empty (LAC-004), progresso A/B (LAC-002), preview link (LAC-003),
 * copy propagação + verificar (LAC-005), export DNS (LAC-009).
 */
const SendingSetupPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  document.title = t("settings.sending.documentTitle");
  const helpHref = helpUrlForRoute(
    "/settings/sending",
    i18n.language || "pt-BR"
  );

  const scrollToStep = (step: SendingSetupStepLetter) => {
    const id =
      step === "A"
        ? "sending-dns"
        : step === "B"
          ? "sending-sender"
          : "sending-ssl";
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const [setup, setSetup] = useState<SendingSetup | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Banner pós-signup: some uma vez (state da navegação não sobrevive a F5 — ok).
  const [showWelcome, setShowWelcome] = useState(
    () => !!(location.state as { fromRegister?: boolean } | null)?.fromRegister
  );
  /** Wizard: um passo por vez; null = ver todos (LAC-111). Preferência em ui_state. */
  const [wizardStep, setWizardStep] = useState<SendingSetupStepLetter | null>(
    "A"
  );
  const [wizardMode, setWizardMode] = useState(true);
  const [wizardPrefsReady, setWizardPrefsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureUiStateLoaded();
      if (cancelled) {
        return;
      }
      const mode = peekUiState().sending_setup_wizard_mode;
      setWizardMode(mode !== "all");
      if (mode === "all") {
        setWizardStep(null);
      }
      setWizardPrefsReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setWizardModePersistent = (nextWizard: boolean) => {
    setWizardMode(nextWizard);
    if (!nextWizard) {
      setWizardStep(null);
    } else if (!wizardStep) {
      setWizardStep("A");
    }
    void persistSendingSetupWizardMode(nextWizard ? "wizard" : "all").then(
      () => {
        toast.success(t("settings.sending.wizard.modeRemembered"), {
          autoClose: 2500,
        });
      }
    );
  };
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSendingSetup();
      if (!res?.success) {
        setError(t("settings.sending.loadError"));
        setSetup(null);
        return;
      }
      setSetup(res.data);
    } catch (e: any) {
      setError(e?.message || t("settings.sending.loadError"));
      setSetup(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const verifyAgain = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await verifySendingSetup();
      if (!res?.success) {
        toast.info(t("settings.sending.verify.stillPending"), {
          autoClose: 3500,
        });
        return null;
      }
      setSetup(res.data);
      toast.info(
        res.data.is_ready
          ? t("settings.sending.verify.ready")
          : t("settings.sending.verify.stillPending"),
        { autoClose: 3500 }
      );
      // Auto-avança para B quando DNS ok (useEffect também alinha passos).
      if (wizardMode && res.data.step_dns_ok) {
        setWizardStep("B");
      }
      return res.data;
    } catch (e: any) {
      toast.error(e?.message || t("settings.sending.loadError"), {
        autoClose: 4000,
      });
      return null;
    } finally {
      setRefreshing(false);
    }
  }, [t, wizardMode]);

  useEffect(() => {
    load();
  }, [load]);

  const isConfigured = Boolean(setup?.tracking_hostname);

  // Ao carregar setup, posiciona o wizard no primeiro passo incompleto.
  useEffect(() => {
    if (!wizardPrefsReady || !setup || !isConfigured || !wizardMode) {
      return;
    }
    if (!setup.step_dns_ok) {
      setWizardStep("A");
    } else if (!setup.step_ses_ok) {
      setWizardStep("B");
    } else if (!setup.step_ssl_ok) {
      setWizardStep("C");
    } else {
      setWizardStep("C");
    }
  }, [
    setup?.step_dns_ok,
    setup?.step_ses_ok,
    setup?.step_ssl_ok,
    isConfigured,
    wizardMode,
    wizardPrefsReady,
  ]);

  const openForm = () => {
    setFieldErrors({});
    setModalOpen(true);
  };

  const submit = async (payload: {
    tracking_hostname: string;
    sending_domain: string;
    from_email: string;
    from_name: string;
    reply_to: string | null;
  }) => {
    setSaving(true);
    setFieldErrors({});
    try {
      const res = await updateSendingSetup(payload);
      if (!res?.success) {
        setFieldErrors({
          tracking_hostname: t("settings.sending.saveError"),
        });
        return;
      }
      setSetup(res.data);
      setModalOpen(false);
      toast.success(res.message || t("settings.sending.saveSuccess"), {
        autoClose: 3000,
      });
    } catch (err: any) {
      const details = err?.details;
      if (details && typeof details === "object") {
        const mapped: Record<string, string> = {};
        Object.keys(details).forEach((k) => {
          mapped[k] = Array.isArray(details[k])
            ? details[k][0]
            : String(details[k]);
        });
        setFieldErrors(mapped);
      } else {
        toast.error(err?.message || t("settings.sending.saveError"), {
          autoClose: 4000,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.sending.pageTitle")}
          actions={
            <PageHelpLink href={helpHref} label={t("settings.sending.help")} />
          }
        />

        {showWelcome && (
          <Alert
            color="success"
            className="mb-3"
            toggle={() => setShowWelcome(false)}
          >
            <div className="fw-medium">{t("auth.register.welcome.title")}</div>
            <div className="small mb-0 mt-1">
              {t("auth.register.welcome.body")}
            </div>
          </Alert>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2 mb-0">{t("settings.sending.loading")}</p>
          </div>
        )}

        {!loading && error && (
          <Alert color="danger" className="d-flex justify-content-between align-items-center">
            <span>{error}</span>
            <Button color="danger" outline size="sm" onClick={() => load()}>
              {t("settings.sending.retry")}
            </Button>
          </Alert>
        )}

        {!loading && !error && setup && !isConfigured && (
          <Row>
            <Col lg={8} className="mx-auto">
              <SendingSetupEmptyState onStart={openForm} />
            </Col>
          </Row>
        )}

        {!loading && !error && setup && isConfigured && (
          <>
            {setup.is_ready ? (
              <Alert color="success" className="mb-3">
                {t("settings.sending.ready.banner")}
              </Alert>
            ) : setup.step_dns_ok && setup.step_ses_ok && setup.step_ssl_ok ? (
              <Alert
                color="info"
                className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2"
              >
                <div>
                  <div className="fw-medium">{t("settings.sending.pending.activatingBanner")}</div>
                  <div className="small mb-0 mt-1">
                    {t("settings.sending.pending.activatingPropagation")}
                  </div>
                </div>
                <Button
                  color="info"
                  outline
                  size="sm"
                  disabled={refreshing}
                  onClick={() => verifyAgain()}
                >
                  {refreshing && <Spinner size="sm" className="me-1" />}
                  {t("settings.sending.verify.again")}
                </Button>
              </Alert>
            ) : setup.step_dns_ok && !setup.step_ses_ok ? (
              // DNS (e às vezes SSL) ok — falta a Amazon confirmar o remetente (até ~24h).
              <Alert
                color="warning"
                className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2"
              >
                <div>
                  <div className="fw-medium">{t("settings.sending.pending.sesBanner")}</div>
                  <div className="small mb-0 mt-1">
                    {t("settings.sending.pending.sesPropagation")}
                  </div>
                </div>
                <Button
                  color="warning"
                  outline
                  size="sm"
                  disabled={refreshing}
                  onClick={() => verifyAgain()}
                >
                  {refreshing && <Spinner size="sm" className="me-1" />}
                  {t("settings.sending.verify.again")}
                </Button>
              </Alert>
            ) : setup.step_dns_ok && setup.step_ses_ok && !setup.step_ssl_ok ? (
              <Alert
                color="info"
                className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2"
              >
                <div>
                  <div className="fw-medium">{t("settings.sending.pending.sslBanner")}</div>
                  <div className="small mb-0 mt-1">
                    {t("settings.sending.pending.sslPropagation")}
                  </div>
                </div>
                <Button
                  color="info"
                  outline
                  size="sm"
                  disabled={refreshing}
                  onClick={() => verifyAgain()}
                >
                  {refreshing && <Spinner size="sm" className="me-1" />}
                  {t("settings.sending.verify.again")}
                </Button>
              </Alert>
            ) : (
              <Alert
                color="info"
                className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2"
              >
                <div>
                  <div className="fw-medium">{t("settings.sending.pending.banner")}</div>
                  <div className="small mb-0 mt-1">
                    {t("settings.sending.pending.propagation")}
                  </div>
                </div>
                <Button
                  color="info"
                  outline
                  size="sm"
                  disabled={refreshing}
                  onClick={() => verifyAgain()}
                >
                  {refreshing && <Spinner size="sm" className="me-1" />}
                  {t("settings.sending.verify.again")}
                </Button>
              </Alert>
            )}

            {setup.dns_delegation_mode === "ns" && (
              <Alert color="primary" className="mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <Badge color="info" className="bg-info-subtle text-info">
                    {t("settings.sending.nsMode.badge")}
                  </Badge>
                  <span className="fw-medium">
                    {t("settings.sending.nsMode.title")}
                  </span>
                </div>
                <div className="small mb-0 mt-1">
                  {t("settings.sending.nsMode.hint")}
                </div>
              </Alert>
            )}

            {setup.dns_delegation_mode !== "ns" &&
              !!setup.dns_diagnostics?.suggest_ns_mode && (
                <Alert color="warning" className="mb-3">
                  <div className="fw-medium">
                    {t("settings.sending.suggestNs.title")}
                  </div>
                  <div className="small mb-0 mt-1">
                    {t("settings.sending.suggestNs.body", {
                      zone: setup.dns_diagnostics.apex_zone || "",
                    })}
                  </div>
                </Alert>
              )}

            {setup.tracking_status === "ssl_ok" && (
              <Alert color="info" className="mb-3">
                <div className="fw-medium">
                  {t("settings.sending.sslPartial.title")}
                </div>
                <div className="small mb-0 mt-1">
                  {t("settings.sending.sslPartial.body")}
                </div>
              </Alert>
            )}

            {!!setup.dns_diagnostics?.conflict_cname && (
              <Alert color="warning" className="mb-3">
                <div className="fw-medium">
                  {t("settings.sending.diagnostics.conflictTitle")}
                </div>
                <div className="small mb-0 mt-1">
                  {t("settings.sending.diagnostics.conflictCname", {
                    found: setup.dns_diagnostics.conflict_cname,
                    expected:
                      setup.dns_delegation_mode === "ns"
                        ? (setup.auth_dns_nameservers || []).join(", ")
                        : setup.tracking_cname_target || "track.brenvio.com.br",
                  })}
                </div>
              </Alert>
            )}

            {setup.dns_delegation_mode === "ns" &&
              !setup.step_dns_ok &&
              setup.dns_diagnostics &&
              !setup.dns_diagnostics.conflict_cname && (
                <Alert color="light" className="mb-3 border">
                  <div className="fw-medium small">
                    {t("settings.sending.diagnostics.diffTitle")}
                  </div>
                  <ul className="small mb-0 mt-2 ps-3">
                    <li>
                      {t("settings.sending.diagnostics.expectedNs")}:{" "}
                      <code>
                        {(setup.dns_diagnostics.expected_ns || []).join(", ") ||
                          "—"}
                      </code>
                    </li>
                    <li>
                      {t("settings.sending.diagnostics.foundNs")}:{" "}
                      <code>
                        {(setup.dns_diagnostics.found_ns || []).join(", ") ||
                          t("settings.sending.diagnostics.none")}
                      </code>
                    </li>
                    <li>
                      {t("settings.sending.diagnostics.expectedA")}:{" "}
                      <code>{setup.dns_diagnostics.expected_a || "—"}</code>
                    </li>
                    <li>
                      {t("settings.sending.diagnostics.foundA")}:{" "}
                      <code>
                        {(setup.dns_diagnostics.found_a || []).join(", ") ||
                          t("settings.sending.diagnostics.none")}
                      </code>
                    </li>
                  </ul>
                </Alert>
              )}

            <SendingSetupProgress
              stepDnsOk={!!setup.step_dns_ok}
              stepSesOk={!!setup.step_ses_ok}
              stepSslOk={!!setup.step_ssl_ok}
              trackingStatus={setup.tracking_status}
              sesStatus={setup.ses_identity_status}
              onStepClick={(step) => {
                if (wizardMode) {
                  setWizardStep(step);
                } else {
                  scrollToStep(step);
                }
              }}
            />

            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
              <div className="btn-group" role="group">
                <Button
                  size="sm"
                  color={wizardMode ? "primary" : "light"}
                  onClick={() => setWizardModePersistent(true)}
                >
                  {t("settings.sending.wizard.modeWizard")}
                </Button>
                <Button
                  size="sm"
                  color={!wizardMode ? "primary" : "light"}
                  onClick={() => setWizardModePersistent(false)}
                >
                  {t("settings.sending.wizard.modeAll")}
                </Button>
              </div>
              {wizardMode && wizardStep ? (
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    color="light"
                    disabled={wizardStep === "A"}
                    onClick={() =>
                      setWizardStep(
                        wizardStep === "C" ? "B" : wizardStep === "B" ? "A" : "A"
                      )
                    }
                  >
                    {t("settings.sending.wizard.back")}
                  </Button>
                  <Button
                    size="sm"
                    color="soft-primary"
                    disabled={wizardStep === "C"}
                    onClick={() =>
                      setWizardStep(
                        wizardStep === "A" ? "B" : wizardStep === "B" ? "C" : "C"
                      )
                    }
                  >
                    {t("settings.sending.wizard.next")}
                  </Button>
                </div>
              ) : null}
            </div>

            {setup.sending_setup_checked_at && (
              <p className="text-muted small mb-3">
                {t("settings.sending.lastChecked", {
                  when: new Date(setup.sending_setup_checked_at).toLocaleString(
                    i18n.language || undefined
                  ),
                })}
              </p>
            )}

            {(!wizardMode || wizardStep === "A") && (
              <SendingSetupDnsTable
                records={setup.dns_records || []}
                delegationMode={setup.dns_delegation_mode || "cname"}
                storageScope={setup.tracking_hostname || ""}
                wizardVerifyCta={wizardMode && wizardStep === "A"}
                verifyBusy={refreshing}
                onMarkAllAndVerify={() => {
                  void verifyAgain();
                }}
              />
            )}

            {wizardMode && (wizardStep === "A" || wizardStep === "B") ? (
              <Alert color="light" className="border mb-3">
                <div className="fw-medium small mb-1">
                  {t("settings.sending.wizard.tagsTipTitle")}
                </div>
                <div className="small mb-2">
                  {t("settings.sending.wizard.tagsTipBody")}
                </div>
                <a
                  href={helpUrlForRoute("/tags", i18n.language || "pt-BR")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small"
                >
                  {t("settings.sending.wizard.tagsTipLink")}
                  <i className="ri-external-link-line ms-1 align-middle" />
                </a>
              </Alert>
            ) : null}

            {(!wizardMode || wizardStep === "B") && (
              <Card className="mb-3" id="sending-sender">
                <CardBody className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <div className="text-muted small">
                      {t("settings.sending.summary.from")}
                    </div>
                    <div className="fw-medium">
                      {setup.from_name} &lt;{setup.from_email}&gt;
                    </div>
                    {setup.reply_to ? (
                      <div className="text-muted small mt-1">
                        {t("settings.sending.summary.replyTo")}:{" "}
                        <code>{setup.reply_to}</code>
                      </div>
                    ) : (
                      <div className="text-warning small mt-1">
                        {t("settings.sending.summary.replyToMissing")}
                      </div>
                    )}
                    <div className="text-muted small mt-1">
                      {t("settings.sending.summary.tracking")}:{" "}
                      <code>{setup.tracking_hostname}</code>
                    </div>
                    {wizardMode ? (
                      <p className="text-muted small mb-0 mt-2">
                        {t("settings.sending.wizard.stepBHint")}
                      </p>
                    ) : null}
                    <Alert
                      color={setup.reply_to ? "info" : "warning"}
                      className="mb-0 mt-3 py-2 small"
                    >
                      <div className="fw-medium mb-1">
                        {t("settings.sending.wizard.replyToTestTitle")}
                      </div>
                      <p className="mb-2">
                        {setup.reply_to
                          ? t("settings.sending.wizard.replyToTestBodyReady", {
                              email: setup.reply_to,
                            })
                          : t("settings.sending.wizard.replyToTestBodyMissing")}
                      </p>
                      <ol className="mb-0 ps-3">
                        <li>{t("settings.sending.wizard.replyToTestStep1")}</li>
                        <li>{t("settings.sending.wizard.replyToTestStep2")}</li>
                        <li>{t("settings.sending.wizard.replyToTestStep3")}</li>
                      </ol>
                    </Alert>
                  </div>
                  <Button color="soft-primary" onClick={openForm}>
                    {t("settings.sending.form.edit")}
                  </Button>
                </CardBody>
              </Card>
            )}

            {(!wizardMode || wizardStep === "C") && setup.tracking_hostname ? (
              <div id="sending-ssl">
                <SendingSetupLinkPreview
                  trackingHostname={setup.tracking_hostname}
                />
                {wizardMode ? (
                  <p className="text-muted small mt-2">
                    {t("settings.sending.wizard.stepCHint")}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        )}

        <SendingSetupFormModal
          open={modalOpen}
          saving={saving}
          setup={setup}
          fieldErrors={fieldErrors}
          onClose={() => setModalOpen(false)}
          onSubmit={submit}
        />

        <ToastContainer closeButton={false} limit={1} />
      </Container>
    </div>
  );
};

export default SendingSetupPage;
